<script setup>
import { MonthInput } from '@addictivegroup/addictive-datetime'
import RecordLink from '@/components/record/RecordLink.vue'
import { computed, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { blobUrl } from '@/lib/http.js'
import { formatDate, formatMoney } from '@/lib/format.js'
import { reportError } from '@/composables/useSave.js'
import { useUi } from '@/stores/ui.js'
import UiButton from '@/components/ui/UiButton.vue'
import UiBadge from '@/components/ui/UiBadge.vue'
import BillingMarks from './BillingMarks.vue'
import { agreementMarks } from './billingMarks.js'

/**
 * Execution and services on a contract's page (D-176): not one figure but what matters — the
 * execution's value and how much of it is accepted, the monthly fee running and in all, the
 * commitment over the minimum terms; the stages and activations on one time line; the agreements
 * themselves; items still to place; the billing schedule as CSV for the ERP.
 */
// The billing schedule's lines (D-216): each agreement's invoicing on its card.
const props = defineProps({ record: { type: Object, required: true }, editable: Boolean, billing: { type: Array, default: () => [] } })
const emit = defineEmits(['changed'])
const { t } = useI18n()
const ui = useUi()
const money = (v) => formatMoney(v ?? 0, props.record.currency_code || 'PLN')
const agreements = computed(() => props.record.agreements || [])
// What a card says of an agreement (D-176): the execution's progress, the services' fees and dates.
function summary(a) {
  if (a.kind === 'execution') {
    const stages = a.stages || []
    const value = stages.reduce((s, x) => s + Number(x.value || 0), 0)
    const accepted = stages.filter((x) => x.status === 'accepted')
    const next = stages.filter((x) => x.status !== 'accepted').map((x) => x.planned_end).filter(Boolean).sort()[0]
    return { progress: stages.length ? accepted.length / stages.length : 0, lines: [
      t('contract.card.stages', { done: accepted.length, all: stages.length }),
      t('contract.card.value', { value: money(value), accepted: money(accepted.reduce((s, x) => s + Number(x.value || 0), 0)) }),
      next ? t('contract.card.nextStage', { date: formatDate(next) }) : null,
      a.due_on ? t('contract.dueOn', { date: formatDate(a.due_on) }) : t('contract.noDue'),
    ].filter(Boolean) }
  }
  const acts = a.activations || []
  const active = acts.filter((x) => x.status === 'active')
  return { progress: acts.length ? active.length / acts.length : 0, lines: [
    t('contract.card.activations', { active: active.length, all: acts.length }),
    t('contract.card.fee', { now: money(active.reduce((s, x) => s + Number(x.fee || 0), 0)), all: money(acts.filter((x) => x.status !== 'ended').reduce((s, x) => s + Number(x.fee || 0), 0)) }),
    [a.min_term_end ? t('contract.minTermEnds', { date: formatDate(a.min_term_end) }) : null, a.notice_deadline ? t('contract.noticeBy', { date: formatDate(a.notice_deadline) }) : null].filter(Boolean).join(' · ') || null,
    a.indexation_rule ? a.indexation_rule.label : t('contract.noIndexation'),
  ].filter(Boolean) }
}
// Versions whose offer's acceptance was undone while the contract was kept (D-176).
const withdrawn = computed(() => (props.record.versions || []).filter((v) => v.withdrawn_at))

const figures = computed(() => {
  const r = props.record
  return [
    { key: 'execution', label: t('contract.figures.execution'), value: money(r.execution_value), sub: t('contract.figures.accepted', { value: money(r.accepted_value) }) },
    { key: 'monthly', label: t('contract.figures.monthly'), value: money(r.monthly_active), sub: t('contract.figures.monthlyTarget', { value: money(r.monthly_fee) }) },
    { key: 'commitment', label: t('contract.figures.commitment'), value: money(r.value), sub: t('contract.figures.commitmentHint') },
  ]
})

// One time line for stages and activations: months across, a bar each.
const timeline = computed(() => {
  const bars = []
  for (const a of agreements.value) {
    // An execution agreement: from its signing to its agreed completion date, the frame its stages are measured against.
    if (a.kind === 'execution' && a.due_on) {
      const from = a.signed_on || (a.stages || []).map((s) => s.started_on || s.planned_start).filter(Boolean).sort()[0] || a.due_on
      bars.push({ key: a.uuid, label: a.number, from, to: a.due_on, tone: 'bg-indigo-300 dark:bg-indigo-700', kind: 'agreement', status: t('contract.dueOn', { date: formatDate(a.due_on) }), due: a.due_on })
    }
    for (const s of a.stages || []) {
      const from = s.started_on || s.planned_start
      const to = s.accepted_on || s.planned_end || from
      // Planned (or accepted) after the agreement's completion date: late.
      const late = !!(a.due_on && to && to > a.due_on)
      bars.push({ key: s.uuid, label: s.name, from, to, tone: late ? 'bg-red-500' : s.status === 'accepted' ? 'bg-green-500' : s.status === 'in_progress' ? 'bg-blue-500' : 'bg-slate-400', kind: 'stage', status: t(`contract.stageStatus.${s.status}`) + (late ? ` · ${t('contract.afterDue', { date: formatDate(a.due_on) })}` : ''), late })
    }
    for (const x of a.activations || []) {
      // Not started yet: when it would start by the plan of the stages it waits for (D-176), drawn as planned.
      const from = x.started_on || x.start_date || x.planned_start
      bars.push({ key: x.uuid, label: x.name, from, to: x.ended_on || null, open: !x.ended_on, tone: x.status === 'active' ? 'bg-emerald-500' : x.status === 'ended' ? 'bg-slate-400' : 'bg-amber-400', planned: !x.started_on, kind: 'activation', status: t(`contract.activationStatus.${x.status}`) })
    }
  }
  const dates = bars.flatMap((b) => [b.from, b.to]).filter(Boolean).sort()
  if (!dates.length) return { bars, months: [] }
  const start = new Date(`${dates[0].slice(0, 7)}-01T00:00:00`)
  const endDate = new Date(`${dates[dates.length - 1]}T00:00:00`)
  const end = new Date(Math.max(endDate.getTime(), new Date(start.getFullYear(), start.getMonth() + 6, 1).getTime()))
  const last = new Date(end.getFullYear(), end.getMonth() + 2, 1)
  const span = last - start
  const pos = (d) => Math.max(0, Math.min(100, ((new Date(`${d}T00:00:00`) - start) / span) * 100))
  const months = []
  for (let m = new Date(start); m < last; m = new Date(m.getFullYear(), m.getMonth() + 1, 1)) months.push({ key: m.toISOString().slice(0, 7), left: ((m - start) / span) * 100, label: m.toLocaleDateString(undefined, { month: 'short', year: m.getMonth() === 0 || months.length === 0 ? '2-digit' : undefined }) })
  return {
    months,
    bars: bars.map((b) => ({ ...b, left: b.from ? pos(b.from) : null, width: b.from ? Math.max(1.5, (b.to ? pos(b.to) : 100) - pos(b.from)) : null })),
  }
})

// The billing schedule for the ERP: from this month, twelve ahead unless told otherwise.
const now = new Date()
const csvFrom = ref(new Date(now.getFullYear(), now.getMonth(), 1).toISOString().slice(0, 7))
const csvTo = ref(new Date(now.getFullYear(), now.getMonth() + 11, 1).toISOString().slice(0, 7))
async function exportCsv() {
  try {
    const url = await blobUrl(`/api/v1/contracts/${props.record.uuid}/billing.csv?from=${csvFrom.value}&to=${csvTo.value}`)
    const a = document.createElement('a')
    a.href = url
    a.download = `${props.record.number || 'kontrakt'}-faktury-${csvFrom.value}-${csvTo.value}.csv`
    a.click()
  } catch (e) {
    reportError(e)
  }
}
// A new agreement of this contract: the create panel from the right, its kind chosen in the form.
function addAgreement() {
  ui.openCreate({ entity: 'agreement', defaults: { contract_uuid: props.record.uuid, _refs: { contract: { uuid: props.record.uuid, label: props.record.label || props.record.title } } }, from: props.record.label || props.record.title, onSaved: () => emit('changed') })
}
</script>

<template>
  <div class="space-y-4" data-testid="contract-scope" data-privacy-cells>
    <div v-if="withdrawn.length" class="rounded-xl border border-red-300 bg-red-50 p-3 text-sm text-red-800 dark:border-red-900 dark:bg-red-950/40 dark:text-red-200" data-testid="contract-withdrawn">
      <i class="fa-solid fa-triangle-exclamation" aria-hidden="true"></i> {{ $t('contract.withdrawnBanner', { n: withdrawn.length }) }}
      <ul class="mt-1 list-inside list-disc text-xs"><li v-for="v in withdrawn" :key="v.uuid">v{{ v.version_no }} · {{ v.offer?.label }} · {{ v.revision_label }} — {{ formatDate(v.withdrawn_at) }}</li></ul>
    </div>
    <section class="grid gap-3 sm:grid-cols-3">
      <div v-for="f in figures" :key="f.key" class="card p-3" :data-testid="`figure-${f.key}`">
        <p class="text-xs font-semibold uppercase tracking-wide text-faint">{{ f.label }}</p>
        <p class="mt-1 text-xl font-semibold tabular-nums">{{ f.value }}</p>
        <p class="text-xs text-muted">{{ f.sub }}</p>
      </div>
    </section>

    <section v-if="timeline.months.length" class="card p-4">
      <h3 class="mb-2 text-sm font-semibold">{{ $t('contract.timeline') }}</h3>
      <div class="relative mb-1 h-5 text-3xs text-faint">
        <span v-for="m in timeline.months" :key="m.key" class="absolute top-0 border-l border-default pl-1" :style="{ left: `${m.left}%` }">{{ m.label }}</span>
      </div>
      <div v-for="b in timeline.bars" :key="b.key" class="relative mb-1 flex h-6 items-center">
        <span :class="['w-40 shrink-0 truncate pr-2 text-xs', b.kind === 'agreement' ? 'font-semibold' : '', b.late ? 'text-red-600' : '']" :title="b.label"><i :class="['fa-solid fa-fw text-3xs text-faint', b.kind === 'agreement' ? 'fa-file-contract' : b.kind === 'stage' ? 'fa-person-digging' : 'fa-arrows-rotate']" aria-hidden="true"></i> {{ b.label }}</span>
        <div class="relative h-3 flex-1 rounded bg-[var(--surface-3)]">
          <div v-if="b.left !== null" :class="['absolute top-0 h-3 rounded', b.tone, b.open ? 'rounded-r-none' : '', b.planned ? 'opacity-60 [background-image:repeating-linear-gradient(45deg,transparent_0_4px,rgba(255,255,255,.55)_4px_8px)]' : '']" :style="{ left: `${b.left}%`, width: `${b.width}%` }" :title="`${b.label}: ${b.status} · ${formatDate(b.from)}${b.to ? ' – ' + formatDate(b.to) : ''}`"></div>
          <span v-else class="absolute left-1 top-0 text-3xs leading-3 text-faint">{{ b.status }}</span>
        </div>
      </div>
    </section>

    <section v-if="record.unplaced?.length" class="card border-amber-300 p-3 text-sm" data-testid="contract-unplaced">
      <p class="font-medium text-amber-700 dark:text-amber-300"><i class="fa-solid fa-triangle-exclamation" aria-hidden="true"></i> {{ $t('contract.unplaced', { n: record.unplaced.length }) }}</p>
      <ul class="mt-1 list-inside list-disc text-xs text-muted"><li v-for="i in record.unplaced" :key="i.uuid">{{ i.name }}</li></ul>
    </section>

    <section class="grid gap-3 md:grid-cols-2">
      <RecordLink v-for="a in agreements" :key="a.uuid" entity="agreement" :uuid="a.uuid" class="card block p-4 text-left transition hover:border-brand-400 hover:shadow" :data-testid="`agreement-card-${a.kind}`">
        <div class="flex flex-wrap items-center gap-2">
          <i :class="['fa-solid', a.kind === 'service' ? 'fa-arrows-rotate' : 'fa-person-digging', 'text-faint']" aria-hidden="true"></i>
          <span class="font-semibold">{{ a.title }}</span>
          <span class="font-mono text-xs text-muted">{{ a.number }}</span>
          <span class="flex-1"></span>
          <UiBadge :tone="a.status === 'in_force' ? 'green' : 'gray'">{{ $t(`contract.agreementStatus.${a.status}`) }}</UiBadge>
        </div>
        <BillingMarks class="mt-2" :marks="agreementMarks(billing, a.uuid)" />
        <div class="mt-2 h-1.5 overflow-hidden rounded bg-[var(--surface-3)]"><div class="h-1.5 rounded bg-green-500" :style="{ width: `${Math.round(summary(a).progress * 100)}%` }"></div></div>
        <ul class="mt-2 space-y-0.5 text-sm text-muted"><li v-for="(l, i) in summary(a).lines" :key="i">{{ l }}</li></ul>
      </RecordLink>
    </section>

    <div class="flex flex-wrap items-center gap-2">
      <template v-if="editable">
        <UiButton size="sm" variant="ghost" icon="fa-plus" data-testid="contract-add-agreement" @click="addAgreement">{{ $t('entities.agreement.new') }}</UiButton>
      </template>
      <span class="flex-1"></span>
      <span class="flex items-center gap-1 text-xs text-muted">{{ $t('contract.billingFrom') }} <MonthInput v-model="csvFrom" class="w-44" :clearable="false" :aria-label="$t('contract.billingFrom')" /></span>
      <span class="flex items-center gap-1 text-xs text-muted">{{ $t('contract.billingTo') }} <MonthInput v-model="csvTo" class="w-44" :min="csvFrom" :clearable="false" :aria-label="$t('contract.billingTo')" /></span>
      <UiButton size="sm" icon="fa-file-csv" data-testid="contract-billing-csv" @click="exportCsv">{{ $t('contract.billingCsv') }}</UiButton>
    </div>
  </div>
</template>
